import { z } from 'zod'

export const siteType = z.enum(['wordpress', 'snippet'])

export const site = z.object({
    id: z.string(),
    type: siteType.default('wordpress'),
    name: z.string(),
    url: z.string(),
    host: z.string(),
    locale: z.string().optional(),
    connected: z.boolean(),
    pluginVersion: z.string().nullable(),
    wpVersion: z.string().nullable(),
    lastSeenAt: z.coerce.date().nullable(),
    lastSyncedAt: z.coerce.date().nullable(),
    openVacanciesCount: z.number().optional(),
    vacanciesCount: z.number().optional(),
    chatsLast7Days: z.number().optional(),
    recommendedChatsLast7Days: z.number().optional(),
    chatsPerDay: z.array(z.number()).optional(),
    health: z.enum(['ok', 'warning', 'error']).optional(),
    issue: z
        .enum(['disconnected', 'no_plan', 'not_seen', 'not_installed', 'chat_disabled', 'documents_failed'])
        .nullable()
        .optional(),
    failedDocumentsCount: z.number().optional(),
    chatEnabled: z.boolean().nullable().optional(),
    candidatesInWordpress: z.boolean().optional(),
    publicKey: z.string().nullable().optional(),
    embedSeenAt: z.coerce.date().nullable().optional(),
    lastCrawledAt: z.coerce.date().nullable().optional(),
    disconnectedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
})

export const siteVacancy = z.object({
    id: z.string(),
    url: z.string().optional(),
    title: z.string(),
    city: z.string().nullable(),
    hoursMin: z.number().nullable(),
    hoursMax: z.number().nullable(),
    education: z.string().nullable(),
    status: z.enum(['open', 'closed', 'removed']),
    validThrough: z.coerce.date().nullable(),
    campaignId: z.string().nullable().optional(),
    campaignUrl: z.string().nullable().optional(),
    applyUrl: z.string().nullable().optional(),
    applyButtonFound: z.boolean().nullable().optional(),
    applyButtonLabel: z.string().nullable().optional(),
    candidates: z
        .object({
            total: z.number(),
            recent: z.number(),
        })
        .nullable()
        .optional(),
})

export const chatCard = z.object({
    type: z.string(),
    id: z.string(),
    title: z.string(),
    details: z.string().array(),
    url: z.string(),
    actions: z
        .object({
            type: z.string(),
            url: z.string(),
        })
        .array(),
})

export const chatMessage = z.object({
    id: z.string(),
    role: z.enum(['visitor', 'assistant']),
    content: z.string(),
    answered: z.boolean().nullable().optional(),
    cards: chatCard.array(),
    createdAt: z.coerce.date(),
})

export const webChat = z.object({
    id: z.string(),
    pageUrl: z.string().nullable(),
    locale: z.string().nullable(),
    visitorMessageCount: z.number(),
    unansweredCount: z.number().optional(),
    recommendationCount: z.number().optional(),
    applyClickCount: z.number().optional(),
    firstQuestion: z.string().nullable().optional(),
    messages: chatMessage.array().optional(),
    pageTitle: z.string().nullable().optional(),
    clickedIds: z.string().array().optional(),
    lastMessageAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
})

const numbers = z.object({
    chats: z.number(),
    visitorMessages: z.number(),
    recommendations: z.number(),
    applyClicks: z.number(),
    unanswered: z.number(),
})

export const siteOverview = z.object({
    health: z.enum(['ok', 'warning', 'error']),
    connection: z.object({
        type: siteType.default('wordpress'),
        connected: z.boolean(),
        pluginVersion: z.string().nullable(),
        latestPluginVersion: z.string().optional(),
        pluginOutdated: z.boolean().optional(),
        wordpressAdminUrl: z.string().nullable().optional(),
        snippet: z.string().optional(),
        csp: z.string().array().optional(),
        lastSeenAt: z.coerce.date().nullable(),
        embedSeenAt: z.coerce.date().nullable().optional(),
        lastSyncedAt: z.coerce.date().nullable(),
        crawling: z.boolean().optional(),
        chatEnabled: z.boolean().nullable(),
    }),
    content: z.object({
        fetched: z.number(),
        pending: z.number(),
        failed: z.number(),
        needsJavascript: z.number().default(0),
        skipped: z.number().default(0),
        indexed: z.number(),
        // The pages Evaa reads against the limit, and the pages left out because of it.
        pages: z.number().default(0),
        maxPages: z.number().default(500),
        limitReached: z.number().default(0),
        openVacancies: z.number(),
        interviewAgents: z.number(),
    }),
    numbers: z.object({
        last7Days: numbers,
        last30Days: numbers,
    }),
    usage: z.object({
        visitorMessages: z.number(),
        companyMessages: z.number(),
        allowance: z.number(),
        extraMessages: z.boolean(),
        resetsAt: z.coerce.date().optional(),
    }),
    todos: z
        .object({
            key: z.string(),
            count: z.number().optional(),
            max: z.number().optional(),
        })
        .array(),
})

export const siteDocument = z.object({
    id: z.string(),
    title: z.string(),
    url: z.string(),
    kind: z.enum(['vacancy', 'content']),
    // Why a page Evaa read itself is a vacancy: a rule from the Knowledge page (vacancyRule), or its vacancy data.
    vacancyRule: z.string().nullable().optional(),
    notVacancyRule: z.string().nullable().optional(),
    vacancyBy: z.enum(['rule', 'detected']).nullable().optional(),
    postType: z.string(),
    status: z.enum(['pending', 'fetched', 'failed', 'removed', 'needs_javascript', 'skipped', 'limit_reached']),
    // The rule that skips the page, which reading it again removes.
    skipRule: z.string().nullable().optional(),
    indexed: z.boolean(),
    error: z.string().nullable(),
    fetchedAt: z.coerce.date().nullable(),
    modifiedAt: z.coerce.date().nullable(),
})

export const assistantVoice = z.object({
    tone: z.enum(['website', 'custom']),
    address: z.enum(['je', 'u']),
    style: z.enum(['short', 'warm']),
    emoji: z.boolean(),
    instructions: z.string().nullable(),
    fallback: z.string().nullable(),
    suggestions: z.string().array().nullable(),
    profile: z
        .object({
            address: z.enum(['je', 'u', 'mixed']),
            style: z.enum(['short', 'warm']),
            emoji: z.boolean(),
            summary: z.string(),
            example: z.string(),
            suggestions: z.string().array(),
            welcome: z.string(),
            analyzedAt: z.string(),
        })
        .nullable(),
    effective: z.object({
        address: z.enum(['je', 'u']),
        style: z.enum(['short', 'warm']),
        emoji: z.boolean(),
        suggestions: z.string().array(),
    }),
})

export const assistantSettings = z.object({
    assistantName: z.string().nullable(),
    welcomeMessage: z.string().nullable(),
    defaultAssistantName: z.string(),
    defaultWelcomeMessage: z.string(),
    capabilities: z
        .object({
            key: z.string(),
            available: z.boolean(),
            enabled: z.boolean(),
        })
        .array(),
    voice: assistantVoice,
})

export const tryOutAnswer = z.object({
    message: z.string(),
    answered: z.boolean(),
    cards: chatCard.array(),
})

export const whatsappSettings = z.object({
    screenings: z.number().optional(),
    differentStartMessage: z.number().optional(),
    selected: z.object({
        whatsappBusinessAccountId: z.string().nullable(),
        phoneNumberId: z.string().nullable(),
        startMessageTemplateId: z.string().nullable(),
    }),
    accounts: z
        .object({
            id: z.string(),
            name: z.string(),
            phoneNumbers: z
                .object({
                    id: z.string(),
                    name: z.string(),
                    number: z.string(),
                })
                .array(),
            startTemplates: z
                .object({
                    id: z.string(),
                    title: z.string(),
                    body: z.string(),
                })
                .array(),
        })
        .array(),
})

export const connectRequest = z.object({
    siteUrl: z.string().url(),
    siteName: z.string().min(1),
    redirectUri: z.string().url(),
    state: z.string().min(1),
    codeChallenge: z.string().min(43),
    codeChallengeMethod: z.literal('S256'),
    candidatesInWordpress: z.boolean().optional(),
    confirmExtraWebsite: z.boolean().optional(),
})

// How many websites are connected and allowed, and what an extra website costs (amounts in cents).
export const siteLimit = z.object({
    connected: z.number(),
    connectedHosts: z.array(z.string()),
    limit: z.number(),
    extraAvailable: z.boolean(),
    extraWebsite: z
        .object({
            amount: z.number(),
            period: z.enum(['monthly', 'yearly']),
            currency: z.string(),
            chargedNow: z.number().nullable(),
        })
        .nullable(),
})

export const unansweredQuestion = z.object({
    question: z.string(),
    count: z.number(),
    chatId: z.string(),
    lastAskedAt: z.coerce.date(),
})

export const siteAnswer = z.object({
    id: z.string(),
    question: z.string(),
    // The visitor's own words, when the question was tidied up.
    askedQuestion: z.string().nullable().optional(),
    answer: z.string(),
    createdBy: z.string().nullable(),
    indexed: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
})

export const chatSettings = z.object({
    enabled: z.boolean(),
    color: z.string(),
    textColor: z.string(),
    position: z.enum(['right', 'left']),
    radius: z.number(),
    label: z.string(),
    credit: z.boolean().nullable(),
    privacyUrl: z.string().nullable(),
    allowedOrigins: z.string().array(),
})

export const crawlSettings = z.object({
    sitemapUrl: z.string().nullable(),
    exclude: z.string().array(),
    vacancyPaths: z.string().array(),
    notVacancyPaths: z.string().array().default([]),
    maxPages: z.number(),
    lastCrawledAt: z.coerce.date().nullable(),
    crawling: z.boolean(),
})

export const installationCheck = z.object({
    installed: z.boolean(),
    inHomepage: z.boolean().nullable(),
    seenAt: z.coerce.date().nullable(),
    error: z.string().nullable(),
})

export const screeningQuestion = z.object({
    text: z.string(),
    autoReject: z.boolean().optional(),
})

export type ChatSettings = z.infer<typeof chatSettings>
export type CrawlSettings = z.infer<typeof crawlSettings>
export type InstallationCheck = z.infer<typeof installationCheck>
export type ScreeningQuestion = z.infer<typeof screeningQuestion>
export type SiteType = z.infer<typeof siteType>
export type UnansweredQuestion = z.infer<typeof unansweredQuestion>
export type SiteAnswer = z.infer<typeof siteAnswer>
export type Site = z.infer<typeof site>
export type SiteVacancy = z.infer<typeof siteVacancy>
export type WebChat = z.infer<typeof webChat>
export type ChatCard = z.infer<typeof chatCard>
export type SiteOverview = z.infer<typeof siteOverview>
export type SiteDocument = z.infer<typeof siteDocument>
export type AssistantSettings = z.infer<typeof assistantSettings>
export type AssistantVoice = z.infer<typeof assistantVoice>
export type VoiceInput = Pick<
    AssistantVoice,
    'tone' | 'address' | 'style' | 'emoji' | 'instructions' | 'fallback' | 'suggestions'
>
export type TryOutAnswer = z.infer<typeof tryOutAnswer>
export type WhatsappSettings = z.infer<typeof whatsappSettings>
export type ChatMessage = z.infer<typeof chatMessage>
export type ConnectRequest = z.infer<typeof connectRequest>
export type SiteLimit = z.infer<typeof siteLimit>
